import { useState } from "react";
import { useLocation } from "react-router-dom";
import { EditBulkClassMutation } from "src/api/Class";
import { EditBulkOrganizationMutation } from "src/api/Organization";
import { BorderButton } from "src/components/buttons/BorderButton";
import InputRadio from "src/components/inputs/InputRadio";
import { InputRadioGroup } from "src/components/inputs/InputRadioGroup";
import { apiClient } from "src/stores/Api";
import { useStore } from "src/stores/Store";
import message from "src/utils/message";
import { modals, useModal } from "src/utils/modals";
import {
  bulkClass,
  editBulkClassVariables,
  editBulkOrganizationVariables,
} from "types/code-generator";
import Modal from "./Modal";

interface Props {
  field:
    | "coupons"
    | "discounts"
    | "agreements"
    | "addons"
    | "infoRequests"
    | "fees"
    | "instructors"
    | "membershipOfferings"
    | "tags"
    | "needs"
    | "communications";
  counts: bulkClass["bulkClass"];
  onNetworkAction: () => void;
  itemId: string;
  itemTitle: string;
  organization?: boolean;
}

export const BulkEditCheckoutModal = modals.create((props: Props) => {
  const modal = useModal();
  const location = useLocation();
  const store = useStore();
  const [enable, setEnable] = useState(null);

  async function onSubmit() {
    if (props.organization) {
      const variables: editBulkOrganizationVariables = {
        data: {},
        filter: location.search,
      };

      if (props.field === "tags") {
        variables.data.tags = {
          itemId: props.itemId,
          add: enable === true,
        };
      } else if (props.field === "needs") {
        variables.data.needs = {
          itemId: props.itemId,
          add: enable === true,
        };
      }

      const processingMessage = message.loading("Updating");

      await apiClient(EditBulkOrganizationMutation, {
        variables,
      });

      processingMessage();
      message.success("Updated!");

      if (props.onNetworkAction) {
        props.onNetworkAction();
      }

      onCancel();
      return;
    }

    const variables: editBulkClassVariables = {
      data: {},
      where: {
        organizationId: store.organization.id,
      },
      filter: location.search,
    };

    if (props.field === "coupons") {
      variables.data.coupons = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "discounts") {
      variables.data.discounts = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "agreements") {
      variables.data.agreements = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "addons") {
      variables.data.addons = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "infoRequests") {
      variables.data.infoRequests = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "fees") {
      variables.data.fees = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "instructors") {
      variables.data.instructors = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "membershipOfferings") {
      variables.data.membershipOfferings = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "tags") {
      variables.data.tags = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    if (props.field === "communications") {
      variables.data.communications = {
        itemId: props.itemId,
        add: enable === true,
      };
    }

    const processingMessage = message.loading("Updating");

    await apiClient(EditBulkClassMutation, {
      variables,
    });

    processingMessage();
    message.success("Updated!");

    if (props.onNetworkAction) {
      props.onNetworkAction();
    }

    onCancel();
  }

  function onCancel() {
    modal.hide();
  }

  let title = "Enable/disable Coupon";
  let enableLabel = "Enable";
  let disableLabel = "Disable";

  const enabled = props.counts[props.field][props.itemId]?.length || 0;
  let type = "offering";

  if (props.organization) {
    type = "organization";
  }

  let description = `Of the selected ${props.counts.total.toLocaleString()} ${type}${
    props.counts.total !== 1 ? "s" : ""
  }, ${enabled.toLocaleString()} ${enabled === 1 ? " has " : " have "} ${
    props.itemTitle
  } enabled.`;

  if (props.field === "discounts") {
    title = "Enable/disable Discount";
  }

  if (props.field === "agreements") {
    title = "Enable/disable Agreements";
  }

  if (props.field === "addons") {
    title = "Enable/disable Add-ons";
  }

  if (props.field === "infoRequests") {
    title = "Enable/disable Info Request";
  }

  if (props.field === "fees") {
    title = "Enable/disable Line Item Fee";
  }

  if (props.field === "instructors") {
    title = `Assign/Unassign ${props.itemTitle}`;
    enableLabel = "Assign";
    disableLabel = "Unassign";
    description = `Of the selected ${props.counts.total.toLocaleString()} offering${
      props.counts.total !== 1 ? "s" : ""
    }, ${enabled.toLocaleString()} ${enabled === 1 ? " has " : " have "} ${
      props.itemTitle
    } assigned.`;
  }

  if (props.field === "membershipOfferings") {
    title = `${props.itemTitle} Availability`;
  }

  if (props.field === "tags") {
    title = "Enable/disable Tag";
  }

  if (props.field === "needs") {
    title = "Enable/disable Need";
  }

  if (props.field === "communications") {
    title = "Enable/disable Communication";
  }

  return (
    <Modal
      visible={modal.visible}
      afterClose={modal.remove}
      title={title}
      handleCancel={onCancel}
      footer={
        <>
          <BorderButton type="submit" onClick={onSubmit}>
            Save & Apply
          </BorderButton>
          <BorderButton onClick={onCancel}>Cancel</BorderButton>
        </>
      }
    >
      <p>{description}</p>

      <hr />

      <div>
        <InputRadioGroup onChange={setEnable} value={enable}>
          <InputRadio
            value={true}
            label={`${enableLabel} for all ${props.counts.total.toLocaleString()} ${type}${
              props.counts.total !== 1 ? "s" : ""
            }`}
          />
          <InputRadio
            value={false}
            label={`${disableLabel} for all ${props.counts.total.toLocaleString()} ${type}${
              props.counts.total !== 1 ? "s" : ""
            }`}
          />
        </InputRadioGroup>
      </div>
    </Modal>
  );
});
